import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Users, CreditCard, Mail, Globe } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "February 12, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500">Last updated: {lastUpdated}</p>
        </div>

        {/* Quick Summary */}
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6 mb-10">
          <h2 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Quick Summary
          </h2>
          <ul className="space-y-2 text-indigo-800">
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-1">•</span>
              We collect only essential data needed to provide our savings club services
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-1">•</span>
              Your financial data is encrypted and securely processed via Stripe and PayPal
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-1">•</span>
              We never sell your personal information to third parties
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-1">•</span>
              You can request deletion of your data at any time
            </li>
          </ul>
        </div>

        {/* Policy Sections */}
        <div className="space-y-10">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">1</span>
              Introduction
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                Welcome to Susu Money ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                By using Susu Money, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">2</span>
              Information We Collect
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Account Information
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Email address (for account creation and communications)</li>
                  <li>• Name (for personalization and club membership)</li>
                  <li>• Profile picture (optional, via Google OAuth)</li>
                  <li>• Phone number (optional, for SMS verification)</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  Financial Information
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Transaction history (contributions, withdrawals, deposits)</li>
                  <li>• Savings club memberships and balances</li>
                  <li>• Protection deposit amounts</li>
                  <li>• Linked bank-account metadata via Plaid: institution name, account type, last 4 of account number, balances, and up to 24 months of transactions</li>
                  <li>• An encrypted Plaid access token (stored at rest with AES-256)</li>
                </ul>
                <p className="text-sm text-gray-500 mt-3 bg-gray-50 p-3 rounded-lg">
                  <strong>What we never store:</strong> your banking username/password, full card numbers, or PayPal credentials. Bank logins are handled end-to-end by Plaid; card payments by Stripe; PayPal transfers by PayPal.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Usage Information
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Device type and operating system</li>
                  <li>• App usage patterns and feature interactions</li>
                  <li>• IP address and approximate location (country level)</li>
                  <li>• Error logs and crash reports</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">3</span>
              How We Use Your Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Provide Services", desc: "Process transactions, manage your savings clubs, and calculate rewards" },
                { title: "Communications", desc: "Send transaction confirmations, security alerts, and important updates" },
                { title: "Improve Experience", desc: "Analyze usage patterns to enhance features and fix issues" },
                { title: "Security", desc: "Detect and prevent fraud, unauthorized access, and illegal activities" },
                { title: "Legal Compliance", desc: "Comply with applicable laws, regulations, and legal processes" },
                { title: "Customer Support", desc: "Respond to your inquiries and resolve issues" },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">4</span>
              Third-Party Services
            </h2>
            <p className="text-gray-600 mb-4">
              We use trusted third-party services to provide our platform. These services have their own privacy policies:
            </p>
            <div className="space-y-3">
              {[
                { name: "Plaid", purpose: "Secure bank-account linking, balance verification, and transaction history. We share only what's needed to verify your contributions. Plaid never shares your banking credentials with us.", link: "https://plaid.com/legal/" },
                { name: "Stripe", purpose: "Payment processing for contributions and deposits", link: "https://stripe.com/privacy" },
                { name: "PayPal", purpose: "Alternative payment processing", link: "https://www.paypal.com/privacy" },
                { name: "Google OAuth", purpose: "Social login authentication", link: "https://policies.google.com/privacy" },
                { name: "MongoDB", purpose: "Secure database storage", link: "https://www.mongodb.com/legal/privacy-policy" },
              ].map((service, index) => (
                <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{service.name}</h4>
                    <p className="text-sm text-gray-500">{service.purpose}</p>
                  </div>
                  <a 
                    href={service.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    View Policy →
                  </a>
                </div>
              ))}
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">5</span>
              Data Security
            </h2>
            <div className="bg-green-50 border border-green-100 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Lock className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-2">Your Data is Protected</h3>
                  <ul className="space-y-2 text-green-800">
                    <li>• All data transmitted is encrypted using TLS/SSL (HTTPS)</li>
                    <li>• Passwords are hashed using bcrypt algorithm</li>
                    <li>• Plaid access tokens are encrypted at rest with AES-256 (Fernet) and are never exposed via our API</li>
                    <li>• Payment data is processed by PCI-DSS compliant providers</li>
                    <li>• Webhook signatures from Plaid are cryptographically verified (ES256 JWT) before any state change</li>
                    <li>• Regular security audits and monitoring</li>
                    <li>• Access controls and authentication for all systems</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">6</span>
              Your Rights
            </h2>
            <p className="text-gray-600 mb-4">
              You have the following rights regarding your personal data:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { title: "Access", desc: "Request a copy of the personal data we hold about you" },
                { title: "Correction", desc: "Request correction of inaccurate personal data" },
                { title: "Deletion", desc: "Request deletion of your account and associated data" },
                { title: "Portability", desc: "Request your data in a machine-readable format" },
                { title: "Opt-Out", desc: "Unsubscribe from marketing communications at any time" },
                { title: "Withdraw Consent", desc: "Withdraw previously given consent for data processing" },
                { title: "Revoke Plaid access", desc: "Disconnect any linked bank from Settings → Bank Accounts at any time" },
                { title: "Right to be forgotten (CCPA / PIPEDA / GDPR)", desc: "Permanently erase your data by going to Settings → Bank Accounts → Delete all my data, or by emailing privacy@susu.money" },
              ].map((right, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-1">{right.title}</h4>
                  <p className="text-sm text-gray-600">{right.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">7</span>
              Data Retention
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                We retain your personal data for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                Transaction records may be retained for up to 7 years for financial compliance purposes. If you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">8</span>
              Children's Privacy
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                Susu Money is designed for users of all ages with appropriate parental guidance for minors. We do not knowingly collect personal information from children under 13 without parental consent. If you are a parent or guardian and believe your child has provided us with personal information without your consent, please contact us.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>School Susu — minors never link a bank.</strong> In the School Susu program, children interact only with virtual coins, badges, and streaks. Plaid bank linking, access-token storage, and any real-money movement are performed strictly at the parent's verified-adult account layer. No minor has a Plaid Item, and Plaid is never asked for a child's financial data. This separation is central to our COPPA, PIPEDA, and GDPR-K compliance posture.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">9</span>
              International Data Transfers
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                Your information may be transferred to and maintained on servers located outside of your country of residence. By using our Service, you consent to the transfer of your information to countries which may have different data protection laws than your country.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-100 text-amber-700 rounded-full text-sm font-bold">10</span>
              Changes to This Policy
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. For significant changes, we will provide a more prominent notice (such as an email notification or in-app alert).
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-indigo-600 text-white rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6" />
              Contact Us
            </h2>
            <p className="text-indigo-100 mb-6">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-3">
              <p className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-300" />
                <span>privacy@susumoney.com</span>
              </p>
              <p className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-300" />
                <span>www.susumoney.com</span>
              </p>
            </div>
            <p className="text-indigo-200 text-sm mt-6">
              We aim to respond to all privacy-related inquiries within 30 days.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 Susu Money. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="text-indigo-600 hover:text-indigo-700 text-sm">Terms of Service</Link>
            <Link to="/" className="text-indigo-600 hover:text-indigo-700 text-sm">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
